import PropTypes from "prop-types";
import React from "react";
import classNames from "classnames";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";

import Dropdown from "components/library/Dropdown";
import Header from "components/library/Header";
import IconButton from "components/library/IconButton";
import Button from "components/library/Button";
import Toolbar from "editor/containers/Toolbar";
import SaveButton from "editor/containers/SaveButton";
import MessagePanel from "components/library/MessagePanel";
import EditorCTA from "editor/containers/EditorCTA";
import EditorLoading from "editor/components/EditorLoading";
import ReadOnlyWrapper from "editor/containers/ReadOnlyWrapper";
import EditorSidebar from "editor/containers/EditorSidebar";
import Metadata from "editor/containers/Metadata";
import NewsletterEditor from "editor/containers/NewsletterEditor";
import SocialSeo from "editor/containers/SocialSeo";
import Publishing from "editor/containers/Publishing";
import PreviousVersions from "editor/containers/PreviousVersions";
import {
  storyInitialState,
  newsletterInitialState,
} from "editor/reducers/form";
import {
  AUTOSAVE_INTERVAL,
  STORY_EDITOR_LINKS,
  URL_TO_API,
  VALIDATION_MESSAGE,
} from "config/constants/editor";
import { STATUS } from "config/constants";

import {
  hydrateForm,
  initializeForm,
  updateFormAt,
  validateFormAsync,
} from "editor/actions/form";
import {
  saveForm,
  setActiveEditor,
  setEditorType,
} from "editor/actions/status";
import { saveAndUnlockForm } from "editor/actions/locking";
import { setMessage } from "actions/message";

import dispatchInterval from "editor/utils/dispatchInterval";
import Log from "../../../utils/log";
import { storeLocalBackup } from "../../../utils/localLogging";
import getJsonState from "../../../utils/getJsonState";
import StoryEditor from "../StoryEditor/StoryEditor";
import styles from "./EditorContainer.css";

class EditorContainer extends React.Component {
  constructor(props) {
    super(props);
    this.getBaseUrl = this._getBaseUrl.bind(this);
    this.editorType = props.match.params.type;
    this.state = { title: null };
    this.shouldSave = this._shouldSave.bind(this);
  }

  UNSAFE_componentWillMount() {
    this.props.setEditorType(URL_TO_API[this.editorType]);
    if (this.editorType === "story") {
      this.props.setActiveEditor("content");
      this.props.initializeForm(storyInitialState);
    } else if (this.editorType === "newsletter") {
      this.props.setActiveEditor("intro");
      this.props.initializeForm(newsletterInitialState);
    }
  }

  componentDidMount() {
    if (this.props.match.params.id && !this.props.match.params.revision) {
      this.props.hydrateForm(this.props.match.params.id).then(() => {
        // Don't update <title> on every keydown.
        // Needs to be in EditorContainer and not StoryEditor/NewsletterEditor so it can use the right componentDidMount method.
        if (this.editorType === "newsletter") {
          this.setState({
            title:
              this.props.editorForm.subject_line ||
              (this.props.editorForm.subscription &&
                this.props.editorForm.subscription.name) ||
              "New newsletter",
          });
        } else if (this.editorType === "story") {
          this.setState({
            title: this.props.editorForm.headline || "New story",
          });
        }
      });
    } else {
      if (this.props.user && this.props.user.id) {
        this.props.updateFormAt("authors", [this.props.user]);
      }
    }
    this.backupInterval = window.setInterval(() => {
      storeLocalBackup(getJsonState(this.props.editorForm));
    }, 300000); // 5 minutes
  }

  _shouldSave() {
    return (
      this.props.status.isDirty &&
      !this.props.status.differentTabLocked &&
      !this.props.status.isReadOnly &&
      !this.props.status.contentOutOfDate
    );
  }

  componentDidUpdate(prevProps) {
    const info = {
      prevStatus: prevProps.status,
      nextStatus: this.props.status,
      autosave: this.autosave,
    };

    if (this.props.editorForm.status === STATUS.DELETED.slug) {
      this.props.history.push("/");
      this.props.setMessage({
        message: `That ${this.editorType} has been deleted`,
      });
    }

    // If the autosave interval is running
    // but there's nothing to save
    // or we aren't allowed to save
    // cancel it
    if (
      typeof this.autosave !== "undefined" &&
      this.autosave.interval &&
      !this.shouldSave()
    ) {
      this.autosave.cancel();
    } else if (
      this.shouldSave() &&
      (typeof this.autosave === "undefined" || !this.autosave.interval)
    ) {
      // If the form is dirty,
      // and there's no reason not to save,
      // and there's no autosave interval set
      // set one
      this.autosave = dispatchInterval(this.props.saveForm, {
        seconds: AUTOSAVE_INTERVAL,
        blocking: true,
      });
    }

    if (!this.autosave && this.shouldSave()) {
      Log.logger(
        "State is dirty, and we are able to save, but autosave isn't running",
        "info",
        info
      );
    }

    // If the ID isn't in the react-router matches and we receive an ID through props, update the URL.
    if (
      !prevProps.match.params.id &&
      !prevProps.editorForm.id &&
      this.props.editorForm.id
    ) {
      const action = prevProps.match.params.action || "";
      prevProps.history.replace(
        this.getBaseUrl(this.props.editorForm.id) + action
      );
    }
  }

  componentWillUnmount() {
    if (this.autosave) {
      this.autosave.cancel();
    }
    if (this.backupInterval) {
      clearInterval(this.backupInterval);
    }
  }

  _getBaseUrl(id = false) {
    const idForUrl = id || this.props.editorForm.id;
    const editorType = this.editorType;
    if (idForUrl) {
      return `/editor/${editorType}/${idForUrl}/`;
    } else {
      return `/editor/${editorType}/`;
    }
  }

  render() {
    const {
      editorForm,
      history,
      isLoading,
      match,
      saveAndUnlockForm,
      status,
    } = this.props;
    const EditorComponent =
      this.editorType === "newsletter"
        ? NewsletterEditor
        : {
            content: StoryEditor,
            metadata: Metadata,
            "social-seo": SocialSeo,
            publishing: Publishing,
            "previous-versions": PreviousVersions,
          }[match.params.action] || StoryEditor;

    // NOTE: Refer to the EditorCTA for full picture on when the schedule button
    // should show.
    let shouldShowScheduleButton = false;

    if (editorForm) {
      if (this.editorType === "newsletter") {
        shouldShowScheduleButton =
          !!editorForm.test_sent && STATUS.SCHEDULED.slug !== editorForm.status;
      } else {
        shouldShowScheduleButton =
          STATUS.READY_FOR_EDITS.slug === editorForm.status ||
          (STATUS.IN_PROGRESS.slug === editorForm.status &&
            !!editorForm.in_edits);
      }
    }

    return (
      <ReadOnlyWrapper status={editorForm.status} id={match.params.id}>
        <Helmet>
          <title>{this.state.title}</title>
        </Helmet>

        <Header
          className={styles.header}
          left={
            <IconButton
              className="gtm-close-out-of-story"
              data-cy="closeOutStoryButton"
              name="x"
              onClick={saveAndUnlockForm}
              to={
                [STATUS.PUBLISHED.slug, STATUS.SCHEDULED.slug].includes(
                  editorForm.status
                )
                  ? "/dashboard/published"
                  : "/dashboard"
              }
            />
          }
          center={
            (this.editorType === "newsletter" ||
              EditorComponent === StoryEditor) && (
              <div className={styles.toolbarHeader}>
                <Toolbar getBaseUrl={this.getBaseUrl} />
              </div>
            )
          }
          right={
            <div className={styles.buttons}>
              <SaveButton />
              {shouldShowScheduleButton ? (
                <Button
                  type="secondary"
                  isDisabled={status.isReadOnly}
                  onClick={() => {
                    return this.props
                      .validateFormAsync(
                        this.props.editorForm,
                        this.editorType === "newsletter",
                        false
                      )
                      .catch(() =>
                        Promise.reject(new Error(VALIDATION_MESSAGE))
                      )
                      .then(() => this.props.saveForm())
                      .then(() =>
                        this.props.history.push(this.getBaseUrl() + "schedule")
                      )
                      .catch(error =>
                        this.props.setMessage({ message: error })
                      );
                  }}
                >
                  Schedule
                </Button>
              ) : null}
              <EditorCTA type={this.editorType} history={history} />
            </div>
          }
        />
        <MessagePanel />
        <div className={styles.editorTopbar}>
          <Dropdown
            inputId="storyEditorLinks"
            onChange={option => {
              history.push(this.getBaseUrl() + option.value);
            }}
            options={STORY_EDITOR_LINKS.map(({ text, to }) => ({
              className: classNames({
                [styles.error]: this.props.editorForm.errors[
                  text.toLowerCase()
                ],
              }),
              label: text,
              value: to,
            }))}
            value={match.params.action}
          />
        </div>
        <div className={styles.editor}>
          {this.editorType === "story" && (
            <EditorSidebar
              links={STORY_EDITOR_LINKS}
              form={this.props.editorForm}
              getBaseUrl={this.getBaseUrl}
            />
          )}
          {isLoading ? (
            <EditorLoading />
          ) : (
            <EditorComponent
              getBaseUrl={this.getBaseUrl}
              id={match.params.id}
              form={this.props.editorForm}
            />
          )}
        </div>
        <div className={styles.toolbarFooter}>
          <Toolbar getBaseUrl={this.getBaseUrl} />
        </div>
      </ReadOnlyWrapper>
    );
  }
}

const mapStateToProps = state => ({
  editorForm: state.editor.form,
  status: state.editor.status,
  isLoading: state.editor.ui.isLoading,
  user: state.user,
});

const mapDispatchToProps = {
  hydrateForm,
  initializeForm,
  saveAndUnlockForm,
  saveForm,
  setActiveEditor,
  setEditorType,
  setMessage,
  updateFormAt,
  validateFormAsync,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EditorContainer);

EditorContainer.propTypes = {
  editorForm: PropTypes.object.isRequired,
  hydrateForm: PropTypes.func.isRequired,
  message: PropTypes.object,
  saveAndUnlockForm: PropTypes.func.isRequired,
  saveForm: PropTypes.func.isRequired,
  status: PropTypes.object,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
  }),
  match: PropTypes.shape({
    params: {
      id: PropTypes.string,
      type: PropTypes.string,
      action: PropTypes.string,
    },
  }),
  validateFormAsync: PropTypes.func.isRequired,
};
